import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Badge from '../../components/Badge.jsx';
import Modal from '../../components/Modal.jsx';
import { Field, Select, TextInput, TextArea } from '../../components/Field.jsx';
import { formatCurrency } from '../../utils/format.js';

const UNITS = ['unit', 'hour', 'day', 'month', 'year'];
const EMPTY = {
  reference: '',
  name: '',
  description: '',
  unit: 'unit',
  unitPrice: 0,
  vatRate: 20,
  category: '',
  active: true,
};

/** The catalogue a quote or invoice line can point at. */
const ProductsPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, refresh } = useApi(() => productApi.list({ q: search }), [search]);

  const submit = async () => {
    setBusy(true);
    try {
      const { id, createdAt, updatedAt, agencyId, ...body } = form;
      if (id) await productApi.update(id, body);
      else await productApi.create(body);
      toast.success(t('common.save'));
      setForm(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Archived, never deleted: a document line may still point at it.
  const archive = async (row) => {
    try {
      await productApi.remove(row.id);
      toast.success(t('products.archived'));
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('products.name'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.name}</span>
          <span className="cell-muted">
            {row.reference ? <code>{row.reference}</code> : null} {row.description?.slice(0, 70)}
          </span>
        </div>
      ),
    },
    { key: 'category', header: t('products.category') },
    {
      key: 'unitPrice',
      header: t('products.unitPrice'),
      className: 'cell-right',
      render: (row) =>
        `${formatCurrency(row.unitPrice, locale, row.currency)} / ${t(`documents.units.${row.unit}`, row.unit)}`,
    },
    { key: 'vatRate', header: t('documents.vat'), className: 'cell-right', render: (row) => `${row.vatRate}%` },
    {
      key: 'active',
      header: t('common.status'),
      render: (row) => (
        <Badge
          value={row.active ? 'ACTIVE' : 'DISABLED'}
          label={row.active ? t('users.statusActive') : t('products.archivedLabel')}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (row) => (
        <div className="row-actions">
          <button type="button" className="btn btn--small" onClick={() => setForm({ ...row })}>
            {t('common.edit')}
          </button>
          {row.active ? (
            <button type="button" className="btn btn--small btn--ghost" onClick={() => archive(row)}>
              {t('products.archive')}
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('products.title')}
        subtitle={t('products.subtitle')}
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setForm({ ...EMPTY })}>
            {t('products.newProduct')}
          </button>
        }
      />

      <Toolbar>
        <TextInput value={search} onChange={setSearch} placeholder={t('common.search')} />
        <span className="toolbar__count">
          {data?.total ?? 0} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable columns={columns} rows={data?.items} loading={loading} emptyTitle={t('common.noData')} />

      <Modal
        open={Boolean(form)}
        title={form?.id ? t('common.edit') : t('products.newProduct')}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={submit} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {form ? (
          <div className="form-grid">
            <Field label={t('products.reference')} htmlFor="p-ref">
              <TextInput id="p-ref" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} />
            </Field>
            <Field label={t('products.category')} htmlFor="p-cat">
              <TextInput id="p-cat" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
            </Field>
            <div className="span-2">
              <Field label={t('products.name')} htmlFor="p-name" required>
                <TextInput id="p-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              </Field>
            </div>
            <div className="span-2">
              <Field label={t('products.description')} htmlFor="p-desc">
                <TextArea id="p-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              </Field>
            </div>
            <Field label={t('products.unitPrice')} htmlFor="p-price" required>
              <TextInput
                id="p-price"
                type="number"
                value={form.unitPrice}
                onChange={(v) => setForm({ ...form, unitPrice: v })}
              />
            </Field>
            <Field label={t('products.unit')} htmlFor="p-unit">
              <Select
                id="p-unit"
                value={form.unit}
                onChange={(v) => setForm({ ...form, unit: v })}
                options={UNITS.map((u) => ({ value: u, label: t(`documents.units.${u}`, u) }))}
              />
            </Field>
            <Field label={t('documents.vat')} htmlFor="p-vat">
              <TextInput
                id="p-vat"
                type="number"
                value={form.vatRate}
                onChange={(v) => setForm({ ...form, vatRate: v })}
              />
            </Field>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default ProductsPage;
